import * as React from 'react';

interface WidgetScopeProps {
  children: React.ReactNode;
  className?: string;
}

const WidgetScope: React.FC<WidgetScopeProps> = ({ children, className = '' }) => {
  const [stylesInjected, setStylesInjected] = React.useState(false);

  React.useEffect(() => {
    if (stylesInjected) return;

    // Check if styles are already injected
    const existingStyles = document.getElementById('widget-styles');
    if (existingStyles) {
      setStylesInjected(true);
      return;
    }

    // Create and inject comprehensive CSS
    const style = document.createElement('style');
    style.id = 'widget-styles';
    style.textContent = `
      /* CSS Variables for widget- prefixed components */
      .widget-scope {
        --widget-background: 0 0% 100%;
        --widget-foreground: 222.2 84% 4.9%;
        --widget-card: 0 0% 100%;
        --widget-card-foreground: 222.2 84% 4.9%;
        --widget-popover: 0 0% 100%;
        --widget-popover-foreground: 222.2 84% 4.9%;
        --widget-primary: 222.2 47.4% 11.2%;
        --widget-primary-foreground: 210 40% 98%;
        --widget-secondary: 210 40% 96%;
        --widget-secondary-foreground: 222.2 84% 4.9%;
        --widget-muted: 210 40% 96%;
        --widget-muted-foreground: 215.4 16.3% 46.9%;
        --widget-accent: 210 40% 96%;
        --widget-accent-foreground: 222.2 84% 4.9%;
        --widget-destructive: 0 84.2% 60.2%;
        --widget-destructive-foreground: 210 40% 98%;
        --widget-border: 214.3 31.8% 91.4%;
        --widget-input: 214.3 31.8% 91.4%;
        --widget-ring: 222.2 84% 4.9%;
        --widget-radius: 0.5rem;
      }

      /* SVG Icon Styling for Widget Components */
      .widget-scope svg {
        display: inline-block;
        fill: currentColor;
        stroke: currentColor;
        stroke-width: 0;
        width: 1em;
        height: 1em;
        vertical-align: middle;
      }

      /* Ensure SVG icons inherit color properly */
      .widget-scope .dsw-text-gray-700 svg {
        color: #374151;
      }

      .widget-scope .dsw-text-white svg {
        color: #ffffff;
      }

      .widget-scope .dsw-text-black svg {
        color: #000000;
      }

      /* Button SVG icon sizing */
      .widget-scope button svg {
        flex-shrink: 0;
      }

      .widget-scope .dsw-h-4 {
        height: 1rem;
      }

      .widget-scope .dsw-w-4 {
        width: 1rem;
      }

      /* Dark mode support */
      @media (prefers-color-scheme: dark) {
        .widget-scope {
          --widget-background: 222.2 84% 4.9%;
          --widget-foreground: 210 40% 98%;
          --widget-card: 222.2 84% 4.9%;
          --widget-card-foreground: 210 40% 98%;
          --widget-popover: 222.2 84% 4.9%;
          --widget-popover-foreground: 210 40% 98%;
          --widget-primary: 210 40% 98%;
          --widget-primary-foreground: 222.2 47.4% 11.2%;
          --widget-secondary: 217.2 32.6% 17.5%;
          --widget-secondary-foreground: 210 40% 98%;
          --widget-muted: 217.2 32.6% 17.5%;
          --widget-muted-foreground: 215 20.2% 65.1%;
          --widget-accent: 217.2 32.6% 17.5%;
          --widget-accent-foreground: 210 40% 98%;
          --widget-destructive: 0 62.8% 30.6%;
          --widget-destructive-foreground: 210 40% 98%;
          --widget-border: 217.2 32.6% 17.5%;
          --widget-input: 217.2 32.6% 17.5%;
          --widget-ring: 212.7 26.8% 83.9%;
        }
      }

      /* Tailwind utility classes with dsw- prefix */
      .widget-scope .dsw-sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
      }

      .widget-scope .dsw-relative {
        position: relative;
      }

      .widget-scope .dsw-absolute {
        position: absolute;
      }

      .widget-scope .dsw-top-1\\/2 {
        top: 50%;
      }

      .widget-scope .dsw-left-2 {
        left: 0.5rem;
      }

      .widget-scope .dsw-right-2 {
        right: 0.5rem;
      }

      .widget-scope .dsw--translate-y-1\\/2 {
        --tw-translate-y: -50%;
        transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
      }

      .widget-scope .dsw-z-10 {
        z-index: 10;
      }

      .widget-scope .dsw-h-8 {
        height: 2rem;
      }

      .widget-scope .dsw-w-8 {
        width: 2rem;
      }

      .widget-scope .dsw-h-4 {
        height: 1rem;
      }

      .widget-scope .dsw-w-4 {
        width: 1rem;
      }

      .widget-scope .dsw-rounded-full {
        border-radius: 9999px;
      }

      .widget-scope .dsw-bg-white {
        background-color: #ffffff;
      }

      .widget-scope .dsw-border {
        border-width: 1px;
        border-color: hsl(var(--widget-border));
      }

      .widget-scope .dsw-shadow-md {
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .widget-scope .dsw-text-gray-700 {
        color: #374151;
      }

      .widget-scope .hover\\:dsw-bg-gray-50:hover {
        background-color: #f9fafb;
      }

      .widget-scope .dsw-overflow-hidden {
        overflow: hidden;
      }

      .widget-scope .dsw-flex {
        display: flex;
      }

      .widget-scope .dsw--ml-4 {
        margin-left: -1rem;
      }

      .widget-scope .dsw--mt-4 {
        margin-top: -1rem;
      }

      .widget-scope .dsw-flex-col {
        flex-direction: column;
      }

      .widget-scope .dsw-min-w-0 {
        min-width: 0px;
      }

      .widget-scope .dsw-shrink-0 {
        flex-shrink: 0;
      }

      .widget-scope .dsw-grow-0 {
        flex-grow: 0;
      }

      .widget-scope .dsw-basis-full {
        flex-basis: 100%;
      }

      .widget-scope .dsw-pl-4 {
        padding-left: 1rem;
      }

      .widget-scope .dsw-pt-4 {
        padding-top: 1rem;
      }

      /* Additional utility classes needed for widget components */
      .widget-scope .dsw-inline-flex {
        display: inline-flex;
      }

      .widget-scope .dsw-items-center {
        align-items: center;
      }

      .widget-scope .dsw-justify-center {
        justify-content: center;
      }

      .widget-scope .dsw-whitespace-nowrap {
        white-space: nowrap;
      }

      .widget-scope .dsw-text-sm {
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .widget-scope .dsw-font-medium {
        font-weight: 500;
      }

      .widget-scope .dsw-ring-offset-background {
        --tw-ring-offset-color: hsl(var(--widget-background));
      }

      .widget-scope .dsw-transition-colors {
        transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
      }

      .widget-scope .dsw-focus-visible\\:dsw-outline-none:focus-visible {
        outline: 2px solid transparent;
        outline-offset: 2px;
      }

      .widget-scope .dsw-focus-visible\\:dsw-ring-2:focus-visible {
        --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
        --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
        box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
      }

      .widget-scope .dsw-focus-visible\\:dsw-ring-ring:focus-visible {
        --tw-ring-color: hsl(var(--widget-ring));
      }

      .widget-scope .dsw-focus-visible\\:dsw-ring-offset-2:focus-visible {
        --tw-ring-offset-width: 2px;
      }

      .widget-scope .dsw-disabled\\:dsw-pointer-events-none:disabled {
        pointer-events: none;
      }

      .widget-scope .dsw-disabled\\:dsw-opacity-50:disabled {
        opacity: 0.5;
      }

      .widget-scope .dsw-cursor-pointer {
        cursor: pointer;
      }
    `;

    document.head.appendChild(style);
    setStylesInjected(true);

    // Cleanup function
    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [stylesInjected]);

  return (
    <div className={`widget-scope ${className}`}>
      {children}
    </div>
  );
};

export default WidgetScope; 